import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { useStore } from '../store';

interface BookmarkState {
  isLoading: boolean;
  error: string | null;
  bookmark: {
    book: string;
    chapter: number;
  } | null;
  loadBookmark: () => Promise<void>;
  updateBookmark: (book: string, chapter: number) => Promise<void>;
}

export const useBookmarkStore = create<BookmarkState>()((set, get) => ({
  isLoading: false,
  error: null,
  bookmark: null,

  loadBookmark: async () => {
    set({ isLoading: true, error: null });
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        // Silently return for unauthenticated users
        return;
      }

      const { data: bookmarkData, error } = await supabase
        .from('bible_bookmarks')
        .select('book, chapter')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 is "no rows returned" error

      if (bookmarkData) {
        set({ bookmark: bookmarkData });
        // Update the global store with the bookmark data
        const store = useStore.getState();
        // Force a state update by setting both values
        store.setSelectedBook(bookmarkData.book);
        store.setSelectedChapter(bookmarkData.chapter);
        // Save to localStorage as a backup
        localStorage.setItem('last-bible-position', JSON.stringify({
          book: bookmarkData.book,
          chapter: bookmarkData.chapter
        }));
      }
    } catch (error) {
      // Only log database errors, not auth-related ones
      if (error instanceof Error && !error.message.includes('authenticated')) {
        set({ error: 'Failed to load bookmark' });
      }
    } finally {
      set({ isLoading: false });
    }
  },

  updateBookmark: async (book: string, chapter: number) => {
    set({ isLoading: true, error: null });
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        // Silently return for unauthenticated users
        return;
      }

      const { error } = await supabase
        .from('bible_bookmarks')
        .upsert(
          { 
            user_id: user.id,
            book,
            chapter,
            updated_at: new Date().toISOString()
          },
          { 
            onConflict: 'user_id',
            ignoreDuplicates: false
          }
        );

      if (error) throw error;
      set({ bookmark: { book, chapter } });
    } catch (error) {
      // Only log database errors, not auth-related ones
      if (error instanceof Error && !error.message.includes('authenticated')) {
        set({ error: 'Failed to update bookmark' });
      }
    } finally {
      set({ isLoading: false });
    }
  },
}));
